export type ObjectiveValueType = 'TARGET_LEVEL' | 'TARGET_IMPROVEMENT' | 'TARGET_REDUCTION';

export const DEFAULT_OBJECTIVE_VALUE_TYPE: ObjectiveValueType = 'TARGET_LEVEL';

/**
 * Accepts current codes and the older LEVEL / IMPROVEMENT / REDUCTION values.
 */
export function normalizeObjectiveValueType(value?: string | null): ObjectiveValueType {
  const code = String(value || '').trim().toUpperCase().replace(/\s+/g, '_');
  if (code === 'TARGET_IMPROVEMENT' || code === 'IMPROVEMENT') {
    return 'TARGET_IMPROVEMENT';
  }
  if (code === 'TARGET_REDUCTION' || code === 'REDUCTION') {
    return 'TARGET_REDUCTION';
  }
  return 'TARGET_LEVEL';
}

export function valueTypeLabelKey(value?: string | null): string {
  const code = normalizeObjectiveValueType(value);
  if (code === 'TARGET_IMPROVEMENT') {
    return 'alignment.objectives.valueTypes.targetImprovement';
  }
  if (code === 'TARGET_REDUCTION') {
    return 'alignment.objectives.valueTypes.targetReduction';
  }
  return 'alignment.objectives.valueTypes.targetLevel';
}

function coerceKpiNumber(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  const cleaned = String(value).trim().replace(/%/g, '').replace(/,/g, '');
  if (cleaned === '') {
    return null;
  }
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
}

function isPercentUnit(unit?: string | null, type?: string | null): boolean {
  const normalizedUnit = (unit || '').trim().toLowerCase();
  const normalizedType = (type || '').trim().toLowerCase();
  return (
    normalizedUnit === '%' ||
    normalizedUnit === 'percent' ||
    normalizedUnit === 'percentage' ||
    normalizedUnit === 'pct' ||
    normalizedType === 'percentage'
  );
}

const NON_PERCENT_UNITS = new Set([
  'usd', '$', 'eur', 'gbp', 'jpy', 'inr', 'count', 'number', '#', 'qty', 'quantity',
  'days', 'day', 'hours', 'hour', 'weeks', 'months', 'fte', 'employees', 'people',
  'units', 'tickets', 'incidents', 'defects'
]);

/**
 * Planning drafts often have a platform KPI name but no unit.
 * Treat ratio-style names as percent unless the catalog unit is clearly not %.
 */
function looksLikePercentName(name?: string | null): boolean {
  const n = (name || '').trim().toLowerCase();
  if (!n) {
    return false;
  }
  return (
    n.includes('percent') ||
    n.includes('percentage') ||
    n.includes('%') ||
    n.includes('margin') ||
    n.includes('market share') ||
    n.includes('share of') ||
    n.includes('availability') ||
    n.includes('uptime') ||
    n.includes('completeness') ||
    n.includes('compliance') ||
    n.includes('reliability') ||
    n.includes('utilization') ||
    n.includes('adoption') ||
    n.includes('satisfaction') ||
    n.includes('nps') ||
    n.includes('progress') ||
    n.includes('churn') ||
    /\brate\b/.test(n) ||
    /\bratio\b/.test(n) ||
    /\byield\b/.test(n) ||
    /\bscore\b/.test(n) ||
    /\bindex\b/.test(n)
  );
}

function isPercentKpi(unit?: string | null, type?: string | null, name?: string | null): boolean {
  if (isPercentUnit(unit, type)) {
    return true;
  }
  const normalizedUnit = (unit || '').trim().toLowerCase();
  if (normalizedUnit && NON_PERCENT_UNITS.has(normalizedUnit)) {
    return false;
  }
  if (
    !normalizedUnit ||
    normalizedUnit === 'index' ||
    normalizedUnit === 'score' ||
    normalizedUnit === 'ratio' ||
    normalizedUnit === 'rate'
  ) {
    return looksLikePercentName(name);
  }
  return false;
}

export function formatObjectiveKpiAmount(
  value: number | string | null | undefined,
  unit?: string | null,
  type?: string | null,
  name?: string | null
): string {
  const numeric = coerceKpiNumber(value);
  if (numeric === null) {
    return '';
  }
  if (isPercentKpi(unit, type, name)) {
    return `${numeric}%`;
  }
  const trimmedUnit = (unit || '').trim();
  if (!trimmedUnit) {
    return String(numeric);
  }
  return `${numeric} ${trimmedUnit}`;
}

function isAcronymWord(word: string): boolean {
  return word.length >= 2 && word === word.toUpperCase() && /[A-Z]/.test(word);
}

function sentenceKpiName(name?: string | null): string {
  const trimmed = (name || '').trim();
  if (!trimmed) {
    return '';
  }
  return trimmed
    .split(/\s+/)
    .map((word) => (isAcronymWord(word) ? word : word.toLowerCase()))
    .join(' ');
}

const VERB: Record<ObjectiveValueType, string> = {
  TARGET_LEVEL: 'Achieve',
  TARGET_IMPROVEMENT: 'Improve',
  TARGET_REDUCTION: 'Reduce'
};

/**
 * Target level: "Achieve 30% operating margin by year 2029"
 * Improve/Reduce: "Improve operating margin by 30% by year 2029"
 */
export function buildObjectiveName(input: {
  valueType?: string | null;
  kpiValue?: number | string | null;
  kpiUnit?: string | null;
  kpiType?: string | null;
  kpiName?: string | null;
  year?: number | null;
}): string {
  const valueType = normalizeObjectiveValueType(input.valueType);
  const verb = VERB[valueType];
  const amount = formatObjectiveKpiAmount(input.kpiValue, input.kpiUnit, input.kpiType, input.kpiName);
  const kpi = sentenceKpiName(input.kpiName);
  const yearBit = input.year ? ` by year ${input.year}` : '';

  let name = '';
  if (valueType === 'TARGET_IMPROVEMENT' || valueType === 'TARGET_REDUCTION') {
    name = [verb, kpi, amount ? `by ${amount}` : ''].filter(Boolean).join(' ');
  } else {
    name = [verb, amount, kpi].filter(Boolean).join(' ');
  }
  name += yearBit;
  return name === verb || name.trim() === '' ? '' : name;
}

export function parseObjectiveKpiInput(value: string | number | null | undefined): number | null {
  return coerceKpiNumber(value);
}
